import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Users, 
  Building2, 
  UserCheck,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  HelpCircle,
  Maximize2,
  Edit,
  Save
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getInitials } from "@/lib/avatar-utils";
import ReactFlow, {
  Node,
  Edge,
  ReactFlowInstance,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  ReactFlowProvider,
  NodeProps,
  Handle,
  Position,
  MarkerType,
  ConnectionLineType,
  BackgroundVariant
} from 'reactflow';
import 'reactflow/dist/style.css';

interface OrgNode {
  id: string;
  name: string;
  email: string;
  designation: string | null;
  department_id: string | null;
  department_name: string | null;
  department_color: string | null;
  governance_level: string | null;
  is_manager: boolean | null;
  reports_to: string | null;
  children: OrgNode[];
  roles: string[];
}

interface Personnel {
  id: string;
  name: string;
  email: string;
  designation: string | null;
  department_id: string | null;
  department_name: string | null;
  department_color: string | null;
  reports_to: string | null;
  manager_name: string | null;
  manager_designation: string | null;
  governance_level: string | null;
  is_manager: boolean | null;
  roles: string[];
  created_at: string;
  status: 'invited' | 'onboarded';
}

interface Department {
  id: string;
  name: string;
  color: string;
}

const getGovernanceLevelColor = (level: string | null) => {
  switch (level) {
    case 'L1': return 'bg-red-100 text-red-800 border-red-200';
    case 'L2': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'L3': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'L4': return 'bg-green-100 text-green-800 border-green-200';
    case 'L5': return 'bg-blue-100 text-blue-800 border-blue-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

// Custom Node Component
const OrgChartNode = ({ data }: NodeProps) => {
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleClick = () => {
    if (data.onNodeClick) {
      data.onNodeClick(data);
    }
  };

  return (
    <div 
      className="bg-white border-2 border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 min-w-[200px] cursor-pointer hover:border-blue-300 hover:scale-105"
      onClick={handleClick}
    >
      <Handle 
        type="target" 
        position={Position.Top} 
        className="!bg-blue-500 !border-2 !border-white !w-3 !h-3"
        style={{ top: -6 }}
      />
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          <Avatar className="w-12 h-12 border-2 border-gray-100 shadow-sm">
            <AvatarFallback className="text-sm font-medium bg-gradient-to-br from-blue-500 to-purple-600 text-white">
              {getInitials(data.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm text-gray-900 truncate">{data.name}</h3>
            <p className="text-xs text-gray-600 truncate font-medium">{data.designation || 'Team Member'}</p>
            {data.department_color && (
              <div className="flex items-center gap-1 mt-1">
                <div 
                  className="w-2 h-2 rounded-full shadow-sm"
                  style={{ backgroundColor: data.department_color }}
                />
                <span className="text-xs text-gray-500 truncate">
                  {data.department_name}
                </span>
              </div>
            )}
          </div>
        </div>
        {data.governance_level && (
          <div className="mt-3 flex justify-center">
            <Badge className={`${getGovernanceLevelColor(data.governance_level)} text-xs font-medium px-2 py-1`}>
              {data.governance_level}
            </Badge>
          </div>
        )}
      </div>
      <Handle 
        type="source" 
        position={Position.Bottom} 
        className="!bg-blue-500 !border-2 !border-white !w-3 !h-3"
        style={{ bottom: -6 }}
      />
    </div>
  );
};

const nodeTypes = {
  orgNode: OrgChartNode,
};

const OrgChartFlow = ({ data, selectedDepartment, onNodeClick }: { data: OrgNode[]; selectedDepartment: string; onNodeClick: (person: any) => void }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [flowInstance, setFlowInstance] = useState<ReactFlowInstance | null>(null);
  const [centerTarget, setCenterTarget] = useState<{ x: number; y: number } | null>(null);

  const buildChartData = useCallback(() => {
    // Filter by department if needed
    const filteredData = selectedDepartment === "all" 
      ? data 
      : data.filter(node => node.department_name === selectedDepartment);

    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];
    
    // Tree layout configuration
    const NODE_WIDTH = 220;
    const NODE_HEIGHT = 120;
    const SIBLING_SPACING = 180; // Vertical spacing between siblings
    const SUBTREE_SPACING = 40; // Additional spacing between subtrees

    // Calculate subtree widths recursively
    const calculateSubtreeWidth = (node: OrgNode): number => {
      if (node.children.length === 0) {
        return NODE_WIDTH;
      }
      
      let totalWidth = 0;
      node.children.forEach((child, index) => {
        const childWidth = calculateSubtreeWidth(child);
        totalWidth += childWidth;
        if (index < node.children.length - 1) {
          totalWidth += SUBTREE_SPACING;
        }
      });
      
      return Math.max(totalWidth, NODE_WIDTH);
    };

    // Calculate positions for all nodes
    const nodePositions = new Map<string, { x: number; y: number }>();
    
    const layoutNode = (node: OrgNode, startX: number, startY: number) => {
      // Keep X based on subtree centering only; level offsets were causing left-start/right-drift.
      const x = startX;
      const y = startY;
      
      nodePositions.set(node.id, { x, y });
      
      if (node.children.length > 0) {
        const subtreeWidth = calculateSubtreeWidth(node);
        const childStartX = startX;
        const childStartY = startY + NODE_HEIGHT + 80; // Vertical spacing to children
        
        let currentX = childStartX;
        
        node.children.forEach((child, index) => {
          const childSubtreeWidth = calculateSubtreeWidth(child);
          const childX = currentX + (childSubtreeWidth - NODE_WIDTH) / 2;
          
          layoutNode(child, childX, childStartY);
          
          currentX += childSubtreeWidth + SUBTREE_SPACING;
        });
      }
    };

    // Layout all root nodes
    let currentY = 100;
    filteredData.forEach((rootNode) => {
      const subtreeWidth = calculateSubtreeWidth(rootNode);
      // Center each root tree around X = 0 instead of left-anchoring from a fixed offset.
      const rootX = -((subtreeWidth - NODE_WIDTH) / 2);
      
      layoutNode(rootNode, rootX, currentY);
      
      // Move to next root position
      currentY += NODE_HEIGHT + 200; // Vertical spacing between root trees
    });

    // Create nodes and edges
    const createNodesAndEdges = (node: OrgNode) => {
      const position = nodePositions.get(node.id);
      if (!position) return;

      newNodes.push({
        id: node.id,
        type: 'orgNode',
        position: { x: position.x, y: position.y },
        data: {
          ...node,
          onNodeClick: onNodeClick,
        },
      });

      // Add edges to children with better styling
      node.children.forEach(child => {
        const childPosition = nodePositions.get(child.id);
        if (childPosition) {
          newEdges.push({
            id: `${node.id}-${child.id}`,
            source: node.id,
            target: child.id,
            animated: true,
            style: { 
              stroke: '#64748b', 
              strokeWidth: 2,
              strokeDasharray: '5,5'
            },
            type: 'smoothstep',
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#64748b',
            },
          });
        }
        createNodesAndEdges(child);
      });
    };

    // Build the chart
    filteredData.forEach(rootNode => {
      createNodesAndEdges(rootNode);
    });

    const firstRoot = filteredData[0];
    if (firstRoot) {
      const firstRootPosition = nodePositions.get(firstRoot.id);
      if (firstRootPosition) {
        setCenterTarget({
          x: firstRootPosition.x + NODE_WIDTH / 2,
          y: firstRootPosition.y + NODE_HEIGHT / 2,
        });
      }
    }

    setNodes(newNodes);
    setEdges(newEdges);
  }, [data, selectedDepartment, setNodes, setEdges, onNodeClick]);

  useEffect(() => {
    buildChartData();
  }, [buildChartData]);

  useEffect(() => {
    if (!flowInstance || !centerTarget) return;
    const frame = window.requestAnimationFrame(() => {
      flowInstance.setCenter(centerTarget.x, centerTarget.y, { zoom: 1, duration: 250 });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [flowInstance, centerTarget]);

  return (
    <div style={{ width: '100%', height: '700px' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onInit={setFlowInstance}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitViewOptions={{
          padding: 0.1,
          includeHiddenNodes: false,
          minZoom: 0.1,
          maxZoom: 2
        }}
        attributionPosition="bottom-left"
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#64748b', strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#64748b',
          },
        }}
        connectionLineType={ConnectionLineType.SmoothStep}
        snapToGrid={true}
        snapGrid={[20, 20]}
      >
        <Background 
          color="#f1f5f9" 
          gap={20} 
          size={1}
          variant={BackgroundVariant.Dots}
        />
        <Controls 
          showZoom 
          showFitView 
          showInteractive={false}
          position="top-right"
        />
        <MiniMap 
          nodeStrokeColor="rgb(148, 163, 184)"
          nodeColor="rgb(255, 255, 255)"
          maskColor="rgba(0, 0, 0, 0.1)"
          position="bottom-left"
          zoomable
          pannable
        />
      </ReactFlow>
    </div>
  );
};

const OrgChart = () => {
  const { toast } = useToast();
  const { profile, hasRole } = useAuth();
  const [orgData, setOrgData] = useState<OrgNode[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [managers, setManagers] = useState<Personnel[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPersonnel, setEditingPersonnel] = useState<Personnel | null>(null);
  
  const [formData, setFormData] = useState({
    designation: "",
    department_id: "",
    reports_to: "",
    governance_level: "L3",
    roles: [] as string[]
  });

  const isAdmin = hasRole ? hasRole('admin') : false;

  // Load organizational data
  const loadOrgData = async () => {
    if (!profile?.company_id) return;
    
    setLoading(true);
    try {
      // Get personnel with department and manager info
      const { data: personnelData, error: personnelError } = await supabase
        .from('profiles')
        .select(`
          id,
          name,
          email,
          designation,
          department_id,
          reports_to,
          governance_level,
          is_manager,
          status,
          created_at,
          updated_at,
          departments (
            name,
            color
          )
        `)
        .eq('company_id', profile.company_id)
        .order('governance_level', { ascending: true })
        .order('created_at', { ascending: false });

      if (personnelError) throw personnelError;

      // Get roles for each person
      const personnelWithRoles = await Promise.all(
        (personnelData || []).map(async (person) => {
          const { data: rolesResult } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', person.id);
          
          return {
            ...person,
            department_name: person.departments?.name || null,
            department_color: person.departments?.color || null,
            roles: rolesResult?.map(r => r.role) || [],
            children: [] as OrgNode[]
          };
        })
      );

      // Build tree structure
      const nodeMap = new Map(personnelWithRoles.map(p => [p.id, { ...p, children: [] }]));
      const roots: OrgNode[] = [];

      personnelWithRoles.forEach(person => {
        const node = nodeMap.get(person.id)!;
        if (person.reports_to && nodeMap.has(person.reports_to)) {
          const parent = nodeMap.get(person.reports_to)!;
          parent.children.push(node);
        } else {
          roots.push(node);
        }
      });

      setOrgData(roots);
      
      // Set managers for the edit dialog - include all onboarded personnel
      // This allows users to report to anyone in the organization
      const managersData = personnelWithRoles
        .filter(p => (p.status || 'onboarded') === 'onboarded')
        .map(p => ({
          id: p.id,
          name: p.name,
          email: p.email,
          designation: p.designation,
          department_id: p.department_id,
          department_name: p.department_name,
          department_color: p.department_color,
          reports_to: p.reports_to,
          manager_name: null,
          manager_designation: null,
          governance_level: p.governance_level,
          is_manager: p.is_manager,
          roles: p.roles,
          created_at: p.created_at,
          status: (p.status || 'onboarded') as 'onboarded' | 'invited'
        }));
      setManagers(managersData);

    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to load organizational data: " + (error.message || "Unknown error"),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Load departments
  const loadDepartments = async () => {
    if (!profile?.company_id) return;
    
    try {
      const { data: departmentsData, error } = await supabase
        .from('departments')
        .select('id, name, color')
        .eq('company_id', profile.company_id)
        .order('name');

      if (error) throw error;
      setDepartments(departmentsData || []);
    } catch (error: any) {
      console.error('Error loading departments:', error);
    }
  };

  useEffect(() => {
    loadOrgData();
    loadDepartments();
  }, [profile?.company_id]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAdmin) {
      toast({
        title: "Permission Denied",
        description: "Only administrators can manage personnel assignments.",
        variant: "destructive"
      });
      return;
    }
    
    if (!editingPersonnel) return;

    try {
      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          designation: formData.designation || null,
          department_id: formData.department_id || null,
          reports_to: formData.reports_to || null,
          governance_level: formData.governance_level,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingPersonnel.id);

      if (profileError) throw profileError;

      // Update roles
      // First, get current roles
      const { data: currentRoles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', editingPersonnel.id);

      const currentRoleValues = (currentRoles?.map(r => r.role) || []) as string[];
      const rolesToAdd = formData.roles.filter(role => !currentRoleValues.includes(role));
      const rolesToRemove = currentRoleValues.filter(role => !formData.roles.includes(role));

      // Add new roles
      for (const role of rolesToAdd) {
        const { error: addError } = await supabase
          .from('user_roles')
          .insert({ 
            user_id: editingPersonnel.id, 
            role: role as "creator" | "reviewer" | "approver" | "admin" | "viewer",
            company_id: profile?.company_id
          });
        if (addError) throw addError;
      }

      // Remove old roles
      for (const role of rolesToRemove) {
        const { error: removeError } = await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', editingPersonnel.id)
          .eq('role', role as "creator" | "reviewer" | "approver" | "admin" | "viewer");
        if (removeError) throw removeError;
      }

      toast({
        title: "Success",
        description: "Personnel assignment updated successfully."
      });

      // Reset form and close dialog
      setFormData({ designation: "", department_id: "", reports_to: "", governance_level: "L3", roles: [] });
      setEditingPersonnel(null);
      setIsDialogOpen(false);
      await loadOrgData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to update personnel assignment: " + (error.message || "Unknown error"),
        variant: "destructive"
      });
    }
  };

  // Handle edit
  const handleEdit = (person: any) => {
    if (!isAdmin) {
      toast({
        title: "Permission Denied",
        description: "Only administrators can edit personnel assignments.",
        variant: "destructive"
      });
      return;
    }

    setEditingPersonnel(person);
    setFormData({
      designation: person.designation || "",
      department_id: person.department_id || "",
      reports_to: person.reports_to || "",
      governance_level: person.governance_level || "L3",
      roles: person.roles || []
    });
    setIsDialogOpen(true);
  };

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-32">
            <div className="text-muted-foreground">Loading organizational chart...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Organizational Chart
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Visualize your company's organizational structure and reporting relationships.
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Department Filter */}
              <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter by department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.name}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: dept.color }}
                        />
                        {dept.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {orgData.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Organization Data</h3>
              <p className="text-muted-foreground">
                No team members found in your company to display an organizational chart.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <ReactFlowProvider>
                <OrgChartFlow data={orgData} selectedDepartment={selectedDepartment} onNodeClick={handleEdit} />
              </ReactFlowProvider>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="shadow-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <HelpCircle className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="space-y-2">
              <h3 className="font-medium text-foreground">About Organizational Chart</h3>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>• <strong>Tree Layout:</strong> Hierarchical tree structure with proper branching and spacing</p>
                <p>• <strong>Interactive Chart:</strong> Drag nodes to reposition, zoom in/out, and pan to navigate</p>
                <p>• <strong>Visual Layout:</strong> Each box represents a team member with their role and department</p>
                <p>• <strong>Reporting Lines:</strong> Animated curved lines show reporting relationships with arrows</p>
                <p>• <strong>Department Filter:</strong> Use the dropdown to filter by specific departments</p>
                <p>• <strong>Governance Levels:</strong> Color-coded badges indicate governance level (L1-L5)</p>
                <p>• <strong>Click to Edit:</strong> Click on any person's card to edit their assignment and roles</p>
                <p>• <strong>Auto-Layout:</strong> Automatically calculates optimal positioning for clean tree structure</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Personnel Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) {
          setEditingPersonnel(null);
          setFormData({ designation: "", department_id: "", reports_to: "", governance_level: "L3", roles: [] });
        }
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Personnel Assignment</DialogTitle>
            <DialogDescription>
              Update department assignment and reporting relationship for {editingPersonnel?.name}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="designation">Designation</Label>
                <Input
                  id="designation"
                  value={formData.designation}
                  onChange={(e) => setFormData(prev => ({ ...prev, designation: e.target.value }))}
                  placeholder="e.g. Product Manager, CEO"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Select value={formData.department_id || "none"} onValueChange={(value) => setFormData(prev => ({ ...prev, department_id: value === "none" ? "" : value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Department</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: dept.color }}
                          />
                          {dept.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reports_to">Reports To</Label>
                <Select value={formData.reports_to || "none"} onValueChange={(value) => setFormData(prev => ({ ...prev, reports_to: value === "none" ? "" : value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Manager</SelectItem>
                    {managers.filter(m => m.id !== editingPersonnel?.id).map((manager) => (
                      <SelectItem key={manager.id} value={manager.id}>
                        {manager.name} - {manager.designation || 'Manager'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="governance_level">Governance Level</Label>
                <Select value={formData.governance_level || "L3"} onValueChange={(value) => setFormData(prev => ({ ...prev, governance_level: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select governance level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L1">L1 - Executive Leadership</SelectItem>
                    <SelectItem value="L2">L2 - Senior Management</SelectItem>
                    <SelectItem value="L3">L3 - Management</SelectItem>
                    <SelectItem value="L4">L4 - Senior Individual Contributor</SelectItem>
                    <SelectItem value="L5">L5 - Individual Contributor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="roles">Roles</Label>
                <div className="grid grid-cols-2 gap-2">
                  {['admin', 'creator', 'reviewer', 'approver', 'viewer'].map(role => (
                    <label key={role} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.roles.includes(role)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData(prev => ({ ...prev, roles: [...prev.roles, role] }));
                          } else {
                            setFormData(prev => ({ ...prev, roles: prev.roles.filter(r => r !== role) }));
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm capitalize">{role}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                <Save className="w-4 h-4 mr-2" />
                Update Assignment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgChart;
